<script setup lang="ts">
import { computed } from 'vue';
import AirTrend from './AirTrend.vue';
import { getTierForPsi } from '../api/tiers';
import { useAirBulletin } from '../composables/useAirBulletin';
withDefaults(defineProps<{ showTrend?: boolean }>(), { showTrend: true });
const {
  displayedPsi: psi,
  currentPm25: pm25,
  currentTier: tier,
  regionalReadings: regions,
  updatedLabel,
  isLoading,
  liveData,
  previousDesign,
  feedStatus,
  refresh,
} = useAirBulletin();
const loading = computed(() => isLoading.value && !liveData.value);
</script>

<template>
  <section class="reading-card" aria-label="Current air quality">
    <div class="reading-topline">
      <span class="reading-label">{{
        previousDesign
          ? 'THE STRAIGHT-FACED REPORT'
          : feedStatus === 'fresh'
            ? 'LATEST PSI READING'
            : 'OFFICIAL PSI READING'
      }}</span>
      <span class="reading-updated">{{ updatedLabel }}</span>
    </div>
    <div
      v-if="feedStatus === 'stale' || feedStatus === 'unavailable'"
      class="feed-notice"
      role="status"
    >
      <p>
        {{
          feedStatus === 'stale'
            ? 'This reading is delayed or its time cannot be verified. It may not reflect the current air quality.'
            : 'The official feed is temporarily unavailable.'
        }}
      </p>
      <button class="text-button" type="button" :disabled="isLoading" @click="refresh">
        {{ isLoading ? 'Refreshing…' : 'Try again' }}
      </button>
    </div>
    <div class="reading-main">
      <div class="reading-score">{{ psi === null ? '—' : psi }}</div>
      <div class="reading-units">
        <span class="reading-unit">PSI <span>·</span> 24-HOUR</span>
        <span v-if="tier" class="tier-chip" :data-tier="tier.id">{{ tier.name }}</span>
        <span v-else class="tier-chip pending">{{ loading ? 'READING AIR' : 'NO LIVE DATA' }}</span>
      </div>
    </div>
    <div class="reading-detail">
      <span>{{ previousDesign ? 'Highest regional PSI' : 'Top regional PSI' }}</span>
      <span
        >Average PM2.5 · 24-hour <strong>{{ pm25 === null ? '—' : `${pm25} µg/m³` }}</strong></span
      >
    </div>
    <div class="reading-region-heading">
      <span>PSI BY REGION</span>
      <span>24-HOUR</span>
    </div>
    <dl class="reading-regions" aria-label="24-hour PSI readings by region">
      <div v-for="reading in regions" :key="reading.region" class="reading-region">
        <dt>{{ reading.region }}</dt>
        <dd :data-tier="reading.psi === null ? 'pending' : getTierForPsi(reading.psi).id">
          {{ reading.psi === null ? '—' : reading.psi }}
        </dd>
      </div>
    </dl>
    <p class="reading-advisory">
      {{
        tier?.advisory ??
        (loading
          ? 'Fetching the latest official readings from Data.gov.sg.'
          : 'The official reading is temporarily unavailable.')
      }}
    </p>
    <AirTrend v-if="showTrend" />
  </section>
</template>
